import { ReactNode, useContext, useEffect, useCallback } from "react";
import { Card, styled, Typography } from "@mui/material";
import { useAnalyticsTracker } from "../../app/AnalyticsTracker";
import { PageStatusContext } from "../../app/PageStatusCtx";
import { useCurrentRoute } from "../../builder/mapping/page";
import { useUser } from "../../dataAccess/api/user";
import { defaultFocusOutline } from "../../helpers/accessibility";
import { useTranslation } from "../../helpers/i18n";

interface Props {
  children: ReactNode;
  loading?: boolean;
  skipToMainContentAvailable?: boolean;
}

const SkipNavContainer = styled("nav")(({ theme }) => ({
  position: "absolute",
  top: -1000,
  left: 0,
  zIndex: 99999,
  padding: theme.spacing(2),
  "&:focus-within": {
    top: 0,
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.5),
  maxWidth: "100%",
  background: theme.palette.background.default,
  color: theme.palette.text.default,
  padding: theme.spacing(3),
}));

const SkipTitle = styled(Typography)(({ theme }) => ({
  display: "block",
  fontSize: theme.typography.body1.fontSize,
}));

const SkipLink = styled("a")(({ theme }) => ({
  display: "inline-block",
  padding: theme.spacing(1),
  color: theme.palette.primary.main,
  textDecoration: "none",
  borderRadius: theme.shape.borderRadius,
  "&:focus": {
    outline: defaultFocusOutline(theme),
  },
}));

function Page({ children, loading, skipToMainContentAvailable }: Props) {
  const pageContext = useContext(PageStatusContext);
  const { id: pageType } = useCurrentRoute() || {};
  const { AnalyticsTracker } = useAnalyticsTracker();
  const { t } = useTranslation();
  const { user } = useUser();

  const trackPageLoad = useCallback(() => {
    if (!loading) {
      AnalyticsTracker.trackIfPageLoad({ user, pageType, pageContext });
    }
  }, [loading, user, pageType, pageContext, AnalyticsTracker]);

  const trackPageChange = useCallback(() => {
    if (!loading) {
      AnalyticsTracker.trackIfPageChange({ pageType, pageContext });
    }
  }, [loading, pageType, pageContext, AnalyticsTracker]);

  useEffect(() => {
    trackPageLoad();
  }, [trackPageLoad]);

  useEffect(() => {
    trackPageChange();
  }, [trackPageChange]);

  return (
    <>
      {skipToMainContentAvailable && (
        <SkipNavContainer>
          <StyledCard>
            <SkipTitle variant="h2">{t("accessibility_skip_to")}</SkipTitle>
            <SkipLink href="#skip-main">
              {t("accessibility_main_content")}
            </SkipLink>
          </StyledCard>
        </SkipNavContainer>
      )}
      {children}
    </>
  );
}

export default Page;
